/**
 * ConnectionsPage — manage connected external mailboxes.
 *
 * The entry point for the external-client connect flow: list connected
 * mailboxes with status, add a new one (OAuth or manual IMAP/SMTP), and
 * disconnect. Replaces the legacy `@fias.email` account-creation surface.
 */

import { useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, GripVertical, Loader2, Mail, Plus, Trash2, Pencil } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { type EmailConnection } from '@/lib/api';
import {
  useEmailConnections,
  useDisconnectEmailConnection,
  useReorderEmailConnections,
} from '../hooks/useConnectAccount';
import { ConnectAccountDialog } from '../components/ConnectAccountDialog';
import { EditAccountDialog } from '../components/EditAccountDialog';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  syncing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function providerLabel(c: EmailConnection): string {
  if (c.provider === 'gmail') return 'Gmail';
  if (c.provider === 'microsoft') return 'Outlook';
  return 'IMAP';
}

/**
 * One connected-mailbox row, drag-sortable by its grip handle. The handle owns
 * the drag listeners so the Edit / Disconnect buttons stay clickable.
 */
function SortableConnectionRow({
  connection: c,
  draggable,
  disconnectPending,
  onEdit,
  onDisconnect,
}: {
  connection: EmailConnection;
  draggable: boolean;
  disconnectPending: boolean;
  onEdit: () => void;
  onDisconnect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: c.accountId,
    disabled: !draggable,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between border rounded-lg px-4 py-3 bg-card"
    >
      <div className="flex min-w-0 items-center gap-2">
        {draggable && (
          <button
            type="button"
            className="cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
            aria-label={`Drag to reorder ${c.emailAddress}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{c.emailAddress}</span>
            {c.isPrimary && (
              <Badge variant="outline" className="text-[10px]">
                Primary
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span>{providerLabel(c)}</span>
            <span>·</span>
            <span
              className={`px-1.5 py-0.5 rounded ${STATUS_STYLES[c.syncStatus] ?? STATUS_STYLES.pending}`}
              title={
                c.lastSyncedAt
                  ? `Last synced ${formatDistanceToNow(new Date(c.lastSyncedAt), { addSuffix: true })}`
                  : 'Never synced'
              }
            >
              {c.syncStatus}
            </span>
            {c.lastSyncError && (
              <span className="text-destructive truncate">· {c.lastSyncError}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" className="gap-1" onClick={onEdit}>
          <Pencil className="h-4 w-4" /> Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive gap-1"
          disabled={disconnectPending}
          onClick={onDisconnect}
        >
          <Trash2 className="h-4 w-4" /> Disconnect
        </Button>
      </div>
    </div>
  );
}

export function ConnectionsPage() {
  const navigate = useNavigate();
  const { data: connections, isLoading } = useEmailConnections();
  const disconnect = useDisconnectEmailConnection();
  const reorder = useReorderEmailConnections();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<EmailConnection | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !connections) return;
    const ids = connections.map((c) => c.accountId);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    reorder.mutate(arrayMove(ids, oldIndex, newIndex));
  };

  const canReorder = (connections?.length ?? 0) > 1;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4">
      {/* `..` resolves to the arche's inbox (mirrors MailboxPage's `../connections`). */}
      <Link
        to=".."
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to inbox
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Email accounts</h1>
          <p className="text-sm text-muted-foreground">
            Connect Gmail, Outlook, or any IMAP mailbox.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-1">
          <Plus className="h-4 w-4" /> Add account
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading accounts…
        </div>
      ) : !connections || connections.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Mail className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
          <p className="font-medium">No mailboxes connected yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Connect an account to start reading and sending mail.
          </p>
          <Button onClick={() => setDialogOpen(true)} className="gap-1">
            <Plus className="h-4 w-4" /> Connect an account
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={connections.map((c) => c.accountId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {connections.map((c) => (
                <SortableConnectionRow
                  key={c.accountId}
                  connection={c}
                  draggable={canReorder}
                  disconnectPending={disconnect.isPending}
                  onEdit={() => setEditAccount(c)}
                  onDisconnect={() => {
                    if (window.confirm(`Disconnect ${c.emailAddress}?`)) {
                      disconnect.mutate(c.accountId);
                    }
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ConnectAccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConnected={() => navigate('..')}
      />
      <EditAccountDialog
        open={!!editAccount}
        onOpenChange={(o) => !o && setEditAccount(null)}
        connection={editAccount}
      />
    </div>
  );
}
